import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import CompanyProfileSectionFormDialog from "@/components/dialogs/CompanyProfileSectionFormDialog";
import api, { downloadUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Download, ExternalLink, Leaf } from "lucide-react";

export default function CompanyProfile() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sections, setSections] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);

  const load = () => {
    api.get("/company-profile").then((r) => setSections(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const deleteSection = async (id) => {
    try {
      await api.delete(`/company-profile/${id}`);
      toast.success("Section removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openEdit = (section) => {
    setEditingSection(section);
    setFormOpen(true);
  };

  const openAdd = () => {
    setEditingSection(null);
    setFormOpen(true);
  };

  return (
    <DashboardLayout title="About EnviroWealth">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 text-[#4B5563] text-sm">
          <Leaf className="h-4 w-4 text-[#00823B]" />
          Company mission, key figures and resources — internal to EnviroWealth staff.
        </div>
        {isAdmin && (
          <Button data-testid="add-section-button" onClick={openAdd} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add Section
          </Button>
        )}
      </div>

      {sections.length === 0 && (
        <p className="text-sm text-[#4B5563] text-center py-16" data-testid="company-profile-empty-state">
          No sections added yet. {isAdmin ? "Click \"Add Section\" to get started." : ""}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {sections.filter((s) => s.section_type === "stat").length > 0 && (
          <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4">
            {sections.filter((s) => s.section_type === "stat").map((s) => (
              <StatCard key={s.id} section={s} isAdmin={isAdmin} onEdit={() => openEdit(s)} onDelete={() => deleteSection(s.id)} />
            ))}
          </div>
        )}

        {sections.filter((s) => s.section_type === "text").map((s) => (
          <TextCard key={s.id} section={s} isAdmin={isAdmin} onEdit={() => openEdit(s)} onDelete={() => deleteSection(s.id)} />
        ))}

        {sections.filter((s) => s.section_type === "file").map((s) => (
          <FileCard key={s.id} section={s} isAdmin={isAdmin} onEdit={() => openEdit(s)} onDelete={() => deleteSection(s.id)} />
        ))}

        {sections.filter((s) => s.section_type === "link").map((s) => (
          <LinkCard key={s.id} section={s} isAdmin={isAdmin} onEdit={() => openEdit(s)} onDelete={() => deleteSection(s.id)} />
        ))}
      </div>

      <CompanyProfileSectionFormDialog open={formOpen} onOpenChange={setFormOpen} section={editingSection} onSaved={load} />
    </DashboardLayout>
  );
}

function AdminControls({ onEdit, onDelete, title }) {
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <button data-testid={`section-edit-${title}`} onClick={onEdit} className="h-7 w-7 flex items-center justify-center rounded-md text-[#4B5563] hover:bg-[#F8F9FA] transition-colors duration-200">
        <Pencil className="h-3.5 w-3.5" />
      </button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button data-testid={`section-delete-${title}`} className="h-7 w-7 flex items-center justify-center rounded-md text-red-600 hover:bg-red-50 transition-colors duration-200">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{title}"?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ section, isAdmin, onEdit, onDelete }) {
  return (
    <div data-testid={`profile-section-${section.id}`} className="bg-white border border-[#E2E8F0] rounded-lg p-5 relative group">
      <p className="font-heading text-2xl font-bold text-[#00823B]">{section.stat_value || "—"}</p>
      <p className="text-xs uppercase tracking-[0.1em] text-[#4B5563] mt-1.5">{section.title}</p>
      {isAdmin && (
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <AdminControls onEdit={onEdit} onDelete={onDelete} title={section.title} />
        </div>
      )}
    </div>
  );
}

function TextCard({ section, isAdmin, onEdit, onDelete }) {
  return (
    <div data-testid={`profile-section-${section.id}`} className="bg-white border border-[#E2E8F0] rounded-lg p-5 md:col-span-2">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-heading text-sm font-semibold text-[#1C1C1E]">{section.title}</h3>
        {isAdmin && <AdminControls onEdit={onEdit} onDelete={onDelete} title={section.title} />}
      </div>
      <p className="text-sm text-[#4B5563] mt-2 whitespace-pre-wrap leading-relaxed">{section.content || "—"}</p>
    </div>
  );
}

function FileCard({ section, isAdmin, onEdit, onDelete }) {
  return (
    <div data-testid={`profile-section-${section.id}`} className="bg-white border border-[#E2E8F0] rounded-lg p-5 flex items-center justify-between">
      <div>
        <h3 className="font-heading text-sm font-semibold text-[#1C1C1E]">{section.title}</h3>
        <p className="text-xs text-[#4B5563] mt-1">{section.original_filename || "No file"}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {section.file_path && (
          <a href={downloadUrl(section.file_path, section.original_filename)} data-testid={`section-download-${section.id}`} className="text-[#00823B]">
            <Download className="h-4 w-4" />
          </a>
        )}
        {isAdmin && <AdminControls onEdit={onEdit} onDelete={onDelete} title={section.title} />}
      </div>
    </div>
  );
}

function LinkCard({ section, isAdmin, onEdit, onDelete }) {
  return (
    <div data-testid={`profile-section-${section.id}`} className="bg-white border border-[#E2E8F0] rounded-lg p-5 flex items-center justify-between">
      <div className="min-w-0">
        <h3 className="font-heading text-sm font-semibold text-[#1C1C1E]">{section.title}</h3>
        {section.link_url && (
          <a href={section.link_url} target="_blank" rel="noreferrer" className="text-xs text-[#00823B] hover:underline truncate block mt-1">
            {section.link_url}
          </a>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {section.link_url && (
          <a href={section.link_url} target="_blank" rel="noreferrer" data-testid={`section-open-${section.id}`} className="text-[#00823B]">
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
        {isAdmin && <AdminControls onEdit={onEdit} onDelete={onDelete} title={section.title} />}
      </div>
    </div>
  );
}
